export const SENSOR_SIDES = ['left', 'right'];

/** Shank and thigh placements. Two devices can share a side. */
export const SENSOR_PLACEMENTS = ['LL', 'LU', 'RL', 'RU'];

export const BASE_SENSOR_METRICS = ['isWalk', 'gaitVals_percent'];

export const SENSOR_KEYS_IGNORE = [
  'length',
  'record_index',
  'assessment_type',
  'session_id',
];

/**
 * Returns 'left' | 'right' when the metric name ends with a device-side suffix.
 */
export function parseSensorMetricSide(metricName) {
  if (!metricName || typeof metricName !== 'string') return null;
  const lower = metricName.toLowerCase();
  if (lower.endsWith('_ll') || lower.endsWith('_lu') || lower.endsWith('_left')) return 'left';
  if (lower.endsWith('_rl') || lower.endsWith('_ru') || lower.endsWith('_right')) return 'right';
  return null;
}

/**
 * Display label for sensor metrics. Ensures side suffix is present when known.
 */
export function formatSensorMetricLabel(metricName, sideOverride = null) {
  if (!metricName) return metricName;
  if (parseSensorMetricSide(metricName)) return metricName;
  const side = sideOverride || null;
  if (!side) return metricName;
  return `${metricName}_${side}`;
}

export function buildSensorMetricKey(baseName, side) {
  if (!baseName) return baseName;
  if (!side) return baseName;
  return `${baseName}_${side}`;
}

/**
 * True when both left- and right-suffixed sensor metrics are present.
 */
export function hasDualDeviceSensorData(sensorNames) {
  if (!Array.isArray(sensorNames) || sensorNames.length === 0) return false;
  const hasLeft = sensorNames.some((n) => parseSensorMetricSide(n) === 'left');
  const hasRight = sensorNames.some((n) => parseSensorMetricSide(n) === 'right');
  return hasLeft && hasRight;
}

/**
 * Pick default sensor charts: prefer side-suffixed metrics when available.
 */
export function discoverDefaultSensorMetrics(sensorNames) {
  if (!Array.isArray(sensorNames)) return [];
  const valid = [];
  for (const base of BASE_SENSOR_METRICS) {
    let foundPlacement = false;
    for (const slot of SENSOR_PLACEMENTS) {
      const name = buildSensorMetricKey(base, slot);
      if (sensorNames.includes(name)) {
        valid.push(name);
        foundPlacement = true;
      }
    }
    if (foundPlacement) continue;
    let foundSuffixed = false;
    for (const side of SENSOR_SIDES) {
      const name = buildSensorMetricKey(base, side);
      if (sensorNames.includes(name)) {
        valid.push(name);
        foundSuffixed = true;
      }
    }
    if (!foundSuffixed && sensorNames.includes(base)) {
      valid.push(base);
    }
  }
  return valid;
}

/**
 * Resolve sensor metric entries from get-dict-metrics.
 * Supports suffixed names in dict.sensors and optional dict.sensor_devices metadata.
 */
export function resolveSensorMetricEntries(dict) {
  const raw = dict && Array.isArray(dict.sensors) ? dict.sensors : [];
  const filtered = raw.filter((n) => !SENSOR_KEYS_IGNORE.includes(n));

  if (Array.isArray(dict?.sensor_devices) && dict.sensor_devices.length > 0) {
    const entries = dict.sensor_devices
      .filter((entry) => entry && entry.metric && !SENSOR_KEYS_IGNORE.includes(entry.metric))
      .map((entry) => {
        const side = entry.side === 'left' || entry.side === 'right' ? entry.side : null;
        const key = side ? buildSensorMetricKey(entry.metric, side) : entry.metric;
        return {
          key,
          label: formatSensorMetricLabel(entry.metric, side),
          side,
          apiName: key,
        };
      });
    return entries;
  }

  return filtered.map((name) => ({
    key: name,
    label: formatSensorMetricLabel(name),
    side: parseSensorMetricSide(name),
    apiName: name,
  }));
}

/**
 * Device id for a sensor metric. Placements (LL, LU, RL, RU) and side names
 * stay as their own ids. Any other suffix after a known base is that device's id,
 * so an arbitrary number of EvoWalks do not share a side offset.
 */
export function sensorDeviceId(metricName) {
  if (!metricName || typeof metricName !== 'string') return null;
  const bases = [...BASE_SENSOR_METRICS].sort((a, b) => b.length - a.length);
  for (const base of bases) {
    if (metricName === base) return null;
    if (metricName.startsWith(`${base}_`)) return metricName.slice(base.length + 1);
  }
  const placement = metricName.match(/_(LL|LU|RL|RU)$/);
  if (placement) return placement[1];
  const side = metricName.match(/_(left|right)$/i);
  if (side) return side[1].toLowerCase();
  return null;
}

export function sensorDeviceLabel(deviceId) {
  if (!deviceId || deviceId === 'device') return '';
  if (deviceId === 'left' || deviceId === 'right') {
    return deviceId.charAt(0).toUpperCase() + deviceId.slice(1);
  }
  return deviceId;
}

/** One id per EvoWalk present in the sensor name list, in first-seen order. */
export function listSensorDeviceIds(sensorNames) {
  if (!Array.isArray(sensorNames)) return [];
  const ids = [];
  const seen = new Set();
  for (const name of sensorNames) {
    const id = sensorDeviceId(name);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  if (ids.length === 0 && sensorNames.length > 0) return ['device'];
  return ids;
}

export function getOffsetForSensorMetric(metricName, offsets) {
  if (!offsets || typeof offsets !== 'object') return 0;
  const id = sensorDeviceId(metricName);
  if (id && Object.prototype.hasOwnProperty.call(offsets, id)) {
    const value = offsets[id];
    return value == null ? 0 : value;
  }
  if (!id && Object.prototype.hasOwnProperty.call(offsets, 'device')) {
    return offsets.device ?? 0;
  }
  return 0;
}

/** Backend get-values-metrics / get-sensor-offset flag: series already on video t=0. */
export const CAMERA_WINDOW_ALIGNMENT = 'camera_window';

/**
 * True when the backend already aligned sensors to the camera window
 * (video t=0 = sample window_start). Host handshake must not be stacked.
 */
export function isCameraWindowAlignment(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const raw =
    payload.alignment
    ?? payload.sensor_alignment
    ?? payload.time_alignment;
  return String(raw || '').toLowerCase() === CAMERA_WINDOW_ALIGNMENT;
}

/**
 * Slider values for a trial.
 *
 * Device sync is already in the served sensor series (video t=0). Stored
 * offset_sec_* is that same handshake. Loading it into the plot shifts the
 * device graph a second time, whether or not the response is flagged
 * camera_window. Sliders therefore start at 0. A non-zero value is only a
 * manual residual the clinician applies in this view.
 */
export function initialSensorSliderOffsets() {
  return { left: 0, right: 0 };
}

/**
 * Normalize get-sensor-offset response for single- or dual-device trials.
 */
export function normalizeSensorOffsets(apiResponse, dualFromMetrics = false) {
  const left = apiResponse?.offset_sec_left ?? apiResponse?.offset_sec ?? 0;
  const right = apiResponse?.offset_sec_right ?? 0;
  const dual =
    dualFromMetrics
    || apiResponse?.device_count >= 2
    || (apiResponse?.offset_sec_right != null && apiResponse?.offset_sec_right !== 0)
    || (apiResponse?.offset_sec_left != null && apiResponse?.offset_sec_right != null);
  return { left, right, dual };
}
